import React from 'react';
import { useTranslation } from 'react-i18next';
import FormulaMonthlyImg from '../../../assets/product/oneMonthNew.png';
import FormulaQuarterlyImg from '../../../assets/product/threeMonthsNew.png';
import GiftBagImg from '../../../assets/bag.webp';
import GiftTrackerImg from '../../../assets/habit-tracker.webp';
import { Icons } from '../../elements/icon/resolver';
import { OFFER_TYPE_FORMULA_ABO_UPGRADE, usePriceFormatter } from '../../../utils/orderCompleteUpsell';

const ArrowRightIcon = Icons.checkoutArrowRight;
const BeakerIcon = Icons.checkoutBeaker;
const PouchIcon = Icons.checkoutPouch;

const FormulaUpgradeOffer = ({ postPurchaseUpsell, onAccept, onDecline, isLoading }) => {
  const { t, i18n } = useTranslation();
  const priceFmt = usePriceFormatter(i18n.language);
  const pricing = postPurchaseUpsell?.pricing || {};
  const isAbo = postPurchaseUpsell?.type === OFFER_TYPE_FORMULA_ABO_UPGRADE;
  const variant = isAbo ? 'Abo' : 'OneOff';
  const listAdditionalAmount = Math.max(
    Number((pricing.listMonthlyAmount || 0) * 2),
    Number(pricing.topUpAmount || 0),
  );
  const savingsAmount = Math.max(listAdditionalAmount - Number(pricing.topUpAmount || 0), 0);

  return (
    <section className="t-flex t-flex-col t-gap-3.5">
      <h2 className="!t-m-0 t-font-serif t-text-[23px] t-font-normal t-leading-10 t-text-navy">
        {isAbo ? (
          <>
            {t('UpsellUpgrade_Headline_Abo_Start', 'Upgrade to the')}{' '}
            <em className="t-text-teal-default">{t('UpsellUpgrade_Target_Abo')}</em>{' '}
            {t('UpsellUpgrade_Headline_Abo_End', 'and get')}{' '}
            <mark className="t-rounded t-bg-butter t-px-2 t-text-navy">
              {t('UpsellUpgrade_DiscountText', { percent: pricing.discountPercent })}
            </mark>
            .
          </>
        ) : (
          <>
            {t('UpsellUpgrade_Headline_OneOff_Start', 'Upgrade to the')}{' '}
            <em className="t-text-teal-default">{t('UpsellUpgrade_Target_OneOff')}</em>{' '}
            {t('UpsellUpgrade_Headline_OneOff_End', 'and get')}{' '}
            <mark className="t-rounded t-bg-butter t-px-1 t-text-navy">
              {t('UpsellUpgrade_DiscountText', { percent: pricing.discountPercent })}
            </mark>
            .
          </>
        )}
      </h2>

      <div className="t-flex t-items-center t-gap-2">
        <div className="t-flex t-flex-1 t-flex-col t-items-center t-gap-1.5 t-text-center">
          <span className="t-text-[9.5px] t-font-bold t-uppercase t-leading-tight t-text-gray-500">
            {t('UpsellUpgrade_Current')}
          </span>
          {isAbo ? (
            <img src={FormulaMonthlyImg} alt="" className="t-h-14 t-w-14 t-object-contain" />
          ) : (
            <>
              <span className="t-flex t-h-[52px] t-w-[52px] t-items-center t-justify-center t-rounded-[10px] t-border t-border-gray-200 t-bg-surface t-text-teal-default">
                <PouchIcon className="t-h-8 t-w-8" />
              </span>
              <span className="t-mt-[-4px] t-text-[11px] t-font-bold t-text-gray-500">x30</span>
            </>
          )}
          <span className="t-text-[13px] t-font-bold t-leading-tight t-text-navy">
            {t(`UpsellUpgrade_CurrentVal_${variant}`)}
          </span>
        </div>
        <ArrowRightIcon className="t-h-4 t-w-4 t-flex-shrink-0 t-text-teal-default" />
        <div className="t-flex t-flex-1 t-flex-col t-items-center t-gap-1.5 t-rounded-xl t-border t-border-teal-mid t-bg-teal-bg t-px-2.5 t-py-3.5 t-text-center">
          <span className="t-text-[9.5px] t-font-bold t-uppercase t-leading-tight t-text-teal-default">
            {t('UpsellUpgrade_New')}
          </span>
          {isAbo ? (
            <img src={FormulaQuarterlyImg} alt="" className="t-h-14 t-w-14 t-object-contain" />
          ) : (
            <>
              <span className="t-flex t-h-[52px] t-w-[52px] t-items-center t-justify-center t-rounded-[10px] t-border t-border-gray-200 t-bg-surface t-text-teal-default">
                <PouchIcon className="t-h-8 t-w-8" />
              </span>
              <span className="t-mt-[-4px] t-text-[11px] t-font-bold t-text-gray-500">x90</span>
            </>
          )}
          <span className="t-text-[13px] t-font-bold t-leading-tight t-text-navy">
            {t(`UpsellUpgrade_NewVal_${variant}`)}
          </span>
          <span className="t-text-xs t-font-extrabold t-leading-tight t-text-navy">
            {t('UpsellUpgrade_CompareSave', { amount: priceFmt.format(savingsAmount) })}
          </span>
        </div>
      </div>

      <p className="!t-m-0 t-text-[12.5px] t-leading-normal t-text-gray-500">
        {t(`UpsellUpgrade_TrustLine_${variant}`, {
          current: priceFmt.format(pricing.currentMonthlyAmount || 0),
          list: priceFmt.format(pricing.listMonthlyAmount || 0),
        })}
      </p>
      <p className="!t-m-0 t-text-[12.5px] t-leading-normal t-text-gray-500">
        {t(`UpsellUpgrade_TrustLine2_${variant}`, {
          total: priceFmt.format(pricing.targetTotalAmount || 0),
          listTotal: priceFmt.format((pricing.listMonthlyAmount || 0) * 3),
          perMonth: priceFmt.format(pricing.targetPerMonthAmount || 0),
          list: priceFmt.format(pricing.listMonthlyAmount || 0),
          percent: pricing.discountPercent,
        })}
      </p>
      <p className="!t-m-0 t-flex t-items-start t-gap-1.5 t-text-[11.5px] t-italic t-leading-normal t-text-teal-default">
        <BeakerIcon className="t-mt-0.5 t-h-[15px] t-w-[15px] t-flex-shrink-0" />
        <span>{t('UpsellUpgrade_ScienceNote')}</span>
      </p>

      <div className="t-flex t-flex-col t-gap-1.5 t-rounded-xl t-border t-border-teal-mid t-bg-teal-bg t-p-3">
        <span className="t-text-[9.5px] t-font-extrabold t-uppercase t-tracking-[0.05em] t-text-teal-default">
          {t(`UpsellUpgrade_DealTag_${variant}`)}
        </span>
        <div className="t-flex t-items-baseline t-gap-2">
          <span className="t-text-[12.5px] t-text-gray-500 t-line-through">
            {priceFmt.format(listAdditionalAmount)}
          </span>
          <span className="t-text-[21px] t-font-extrabold t-text-navy">
            {priceFmt.format(pricing.topUpAmount || 0)}
          </span>
        </div>
        <span className="t-w-fit t-rounded-full t-bg-teal-default t-px-2.5 t-py-1 t-text-[11px] t-font-bold t-text-white">
          {t('UpsellUpgrade_SavePill', { amount: priceFmt.format(savingsAmount) })}
        </span>
        <span className="t-text-[11px] t-leading-snug t-text-gray-500">
          {t(`UpsellUpgrade_DealNote2_${variant}`, {
            topUp: priceFmt.format(pricing.topUpAmount || 0),
            total: priceFmt.format(pricing.targetTotalAmount || 0),
            listTotal: priceFmt.format((pricing.listMonthlyAmount || 0) * 3),
            percent: pricing.discountPercent,
          })}
        </span>
        {isAbo && (
          <div className="t-mt-0.5 t-flex t-flex-col t-gap-2 t-border-t t-border-teal-mid t-pt-2.5">
            <div className="t-flex t-items-center t-gap-1.5 t-text-[11.5px] t-font-bold t-text-navy">
              <span aria-hidden="true">🎁</span>
              <span>{t('UpsellUpgrade_GiftsLabel')}</span>
            </div>
            <div className="t-flex t-gap-2">
              {[
                { key: 'Gift_Bag', img: GiftBagImg },
                { key: 'Gift_Tracker', img: GiftTrackerImg },
              ].map((gift) => (
                <div
                  key={gift.key}
                  className="t-flex t-flex-1 t-flex-col t-items-center t-gap-1 t-rounded-[10px] t-border t-border-teal-mid t-bg-white t-px-1.5 t-py-2"
                >
                  <img src={gift.img} alt={t(gift.key)} className="t-h-10 t-w-full t-object-contain" />
                  <span className="t-text-center t-text-[9.5px] t-font-semibold t-leading-tight t-text-navy">
                    {t(gift.key)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="t-flex t-flex-col t-gap-2">
        <button
          type="button"
          onClick={onAccept}
          disabled={isLoading}
          className="t-w-full t-rounded-[11px] t-border-0 t-bg-teal-default t-px-4 t-py-3 t-text-[13.5px] t-font-bold t-text-white hover:t-bg-navy disabled:t-opacity-60"
        >
          {isLoading
            ? t('OrderUpsell_Loading')
            : t(`UpsellUpgrade_Cta_${variant}`, { topUp: priceFmt.format(pricing.topUpAmount || 0) })}
        </button>
        <button
          type="button"
          onClick={onDecline}
          disabled={isLoading}
          className="t-w-full t-border-0 t-bg-transparent t-px-1.5 t-py-1 t-text-center t-text-[11.5px] t-text-gray-500 t-underline disabled:t-opacity-60"
        >
          {t(`UpsellUpgrade_Dismiss_${variant}`)}
        </button>
      </div>
    </section>
  );
};

export default FormulaUpgradeOffer;
